import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
const cityCoordinates: {
  name: string;
  lat: number;
  lng: number;
}[] = [{
  name: 'Scottsdale',
  lat: 33.4942,
  lng: -111.9261
}, {
  name: 'Paradise Valley',
  lat: 33.531,
  lng: -111.9425
}, {
  name: 'Phoenix',
  lat: 33.4484,
  lng: -112.074
}, {
  name: 'Mesa',
  lat: 33.4152,
  lng: -111.8315
}, {
  name: 'Chandler',
  lat: 33.3062,
  lng: -111.8413
}, {
  name: 'Gilbert',
  lat: 33.3528,
  lng: -111.789
}, {
  name: 'Tempe',
  lat: 33.4255,
  lng: -111.94
}, {
  name: 'Queen Creek',
  lat: 33.2487,
  lng: -111.6343
}, {
  name: 'Ahwatukee',
  lat: 33.3392,
  lng: -111.9842
}, {
  name: 'Cave Creek',
  lat: 33.8331,
  lng: -111.9507
}, {
  name: 'Fountain Hills',
  lat: 33.6117,
  lng: -111.7174
}, {
  name: 'Glendale',
  lat: 33.5387,
  lng: -112.186
}, {
  name: 'Peoria',
  lat: 33.5806,
  lng: -112.2374
}, {
  name: 'Surprise',
  lat: 33.6292,
  lng: -112.368
}, {
  name: 'Goodyear',
  lat: 33.4353,
  lng: -112.3577
}, {
  name: 'Anthem',
  lat: 33.8672,
  lng: -112.1476
}];
function createGoldIcon() {
  return L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;background:#B89968;border:2px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7]
  });
}
function createRedIcon() {
  return L.divIcon({
    className: '',
    html: `<div style="width:18px;height:18px;background:#C0392B;border:2px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.35);"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9]
  });
}
export function AreasWeServe() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const cities = cityCoordinates.map(c => c.name);
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      center: [33.5, -111.95],
      zoom: 9,
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: true,
      doubleClickZoom: false
    });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19
    }).addTo(map);
    L.control.zoom({
      position: 'bottomright'
    }).addTo(map);
    const goldIcon = createGoldIcon();
    const redIcon = createRedIcon();
    cityCoordinates.forEach(city => {
      const isHighlight = city.name === 'Scottsdale' || city.name === 'Paradise Valley' || city.name === 'Phoenix';
      const marker = L.marker([city.lat, city.lng], {
        icon: isHighlight ? redIcon : goldIcon
      }).addTo(map);
      marker.bindTooltip(city.name, {
        permanent: false,
        direction: 'top',
        offset: [0, -10],
        className: 'leaflet-tooltip-custom'
      });
    });
    mapInstanceRef.current = map;
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);
  return <section className="py-20 md:py-32 bg-cream" data-id="element-39">
      <style data-id="element-40">{`
        .leaflet-tooltip-custom {
          background: #1A1A1A;
          color: #F8F5F0;
          border: none;
          border-radius: 2px;
          padding: 4px 10px;
          font-family: 'Playfair Display', serif;
          font-size: 13px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
        .leaflet-tooltip-custom::before {
          border-top-color: #1A1A1A;
        }
      `}</style>
      <div className="container mx-auto px-4 md:px-6" data-id="element-41">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-42">
          {/* Map Column */}
          <div className="relative rounded-sm border border-gold/20 shadow-sm overflow-hidden aspect-square md:aspect-[4/3] lg:aspect-square" data-id="element-43">
            <div ref={mapRef} className="w-full h-full" data-id="element-44" />
          </div>

          {/* Content Column */}
          <div data-id="element-45">
            <p className="text-gold text-xs font-bold tracking-[0.2em] uppercase mb-4" data-id="element-46">
              Where We Work
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-charcoal leading-tight mb-6" data-id="element-47">
              Serving every corner of Maricopa County.
            </h2>
            <p className="text-muted-foreground text-lg mb-10 leading-relaxed" data-id="element-48">
              From luxury estates in Paradise Valley to everyday family homes in
              Mesa, we list it all.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-10" data-id="element-49">
              {cities.map((city, index) => <div key={index} className="group cursor-pointer py-2 border-b border-gold/10" data-id="element-50">
                  <span className="font-serif text-lg text-charcoal group-hover:text-brick-red transition-colors relative inline-block" data-id="element-51">
                    {city}
                    <span className="absolute left-0 bottom-0 w-0 h-[1px] bg-gold transition-all duration-300 group-hover:w-full" data-id="element-52"></span>
                  </span>
                </div>)}
            </div>

            <p className="text-sm text-charcoal font-medium bg-white inline-block px-4 py-3 rounded-sm border border-gold/20" data-id="element-53">
              Don't see your area? We likely serve it. Call us at{' '}
              <a href="tel:4802562262" className="text-brick-red hover:underline" data-id="element-54">
                (480) 256-2262
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </section>;
}